import React from 'react';
import { DataAmigavel } from '@/shared/utils/datas';
import { Document, Page, StyleSheet, Text, View, } from '@react-pdf/renderer';
import { IFullReport, IReportComarca, IReportLocalLotacao } from '@/api.types';

// Funções auxiliares
export function isAtivoHoje(data_inicial?: Date | string, data_final?: Date | string): boolean {
  const hoje = new Date();
  const inicio = data_inicial ? new Date(data_inicial) : null;
  const fim = data_final ? new Date(data_final) : null;
  return (
    (!inicio || inicio <= hoje) &&
    (!fim || fim >= hoje)
  );
}

export function getEventos(relatorio: IFullReport, defensorId: number, idDefensoria: number): string {
  const defensor = relatorio.pessoas.find((p) => p.id === defensorId);
  if (!defensor?.abonos) {
    console.warn(`Nenhum abono encontrado para defensor ID ${defensorId}`);
    return '';
  }

  const eventos = defensor.abonos
    .filter((abono) =>
      abono.substitutos.some((s) => s.defensoria_id === idDefensoria),
    )
    .map((folga) => {
      const tipoMap: { [key: number]: string } = {
        1: 'Férias',
        2: 'Folga Natalícia',
        3: 'Abono Trabalhado',
        4: 'Abono Especial',
        5: 'Licença-Prêmio',
        6: 'Licença (Outros)',
        7: 'Afastamento',
      };
      const tipo = tipoMap[folga.tipo] || 'Folga';
      const ini = DataAmigavel(folga.data_inicial);
      const fim = DataAmigavel(folga.data_final);
      const dias = Math.floor(
        (new Date(folga.data_final).getTime() - new Date(folga.data_inicial).getTime()) /
        (1000 * 60 * 60 * 24)
      ) + 1;

      const substitutos = folga.substitutos
        .map((sub) => {
          const subst = relatorio.pessoas.find((p) => p.id === sub.defensor_id);
          return subst ? `${subst.titulo || ''} ${subst.nome}`.trim() : '';
        })
        .filter(Boolean)
        .join(', ');

      return `${tipo}: ${ini} a ${fim} (${dias} dias)${substitutos ? '. Substituto(s): ' + substitutos : ''}`;
    });

  return eventos.join('\n');
}

export function getDefensores(relatorio: IFullReport, defensoriaId: number, selectedDefensores: string[], exibirEventos: boolean) {
  const lotacoes = (relatorio.lotacoes || []).filter(
    (l) =>
      l.defensoria_id === defensoriaId &&
      l.defensor_id &&
      isAtivoHoje(l.data_inicial, l.data_final),
  );

  if (!lotacoes.length) return [<Text key={`vago-${defensoriaId}`}>(VAGO)</Text>];

  const titulares = lotacoes.filter((l) => l.tipo === 1);
  const naoTitulares = lotacoes.filter((l) => l.tipo !== 1);

  const elementos: any[] = [];

  [titulares, naoTitulares].forEach((group) => {
    group.forEach((lot, index) => {
      const defensor = relatorio.pessoas.find((p) => p.id === lot.defensor_id);
      if (!defensor) return;

      const defensorNome = `${defensor.titulo || ''} ${defensor.nome}`.trim();
      if (selectedDefensores.length > 0 && !selectedDefensores.includes(defensorNome)) return;

      const eventos = exibirEventos ? getEventos(relatorio, defensor.id, defensoriaId) : '';
      const anotacoes = [lot.anotacao1, lot.anotacao2].filter(Boolean).join(' - ');

      elementos.push(
        <View key={`L${lot.id}-${index}`} wrap={false} style={{ marginBottom: 4 }}>
          <Text style={{ color: lot.ativo ? 'black' : 'gray' }}>
            {defensorNome}
          </Text>
          {anotacoes && (
            <Text style={{ fontStyle: 'italic', marginTop: 2 }}>Anotações: {anotacoes}</Text>
          )}
          {eventos && exibirEventos && (
            <Text style={{ color: 'red', fontStyle: 'italic', fontSize: 7, marginTop: 2 }}>
              {eventos}
            </Text>
          )}
        </View>
      );
    });
  });

  return elementos.length ? elementos : [<Text key={`vago-${defensoriaId}`}>(VAGO)</Text>];
}

export function getEstagiarios(relatorio: IFullReport, defensoriaId: number) {
  const lotacoes = (relatorio.lotacoes || []).filter(
    (l) => l.defensoria_id === defensoriaId && l.estagio_id && isAtivoHoje(l.data_inicial, l.data_final),
  );

  if (!lotacoes.length) return 'VAGO';

  const estagiarios = lotacoes
    .map((l) => {
      const estagiario = relatorio.pessoas.find((p) =>
        p.estagios?.some((e) => e.id === l.estagio_id),
      );
      if (!estagiario) return null;
      const estagiarioNome = `${estagiario.titulo || ''} ${estagiario.nome}`.trim();
      const dataInicial = DataAmigavel(l.data_inicial);
      const dataFinal = DataAmigavel(l.data_final);
      return `${estagiarioNome}\n(${dataInicial} a ${dataFinal})`;
    })
    .filter(Boolean);

  return estagiarios.length ? estagiarios.join('\n') : 'VAGO';
}

export function getSubstituicoes(relatorio: IFullReport, substituta1_id?: number, substituta2_id?: number) {
  const nome1 = (relatorio.comarcas || [])
    .flatMap((c) => c.locais || [])
    .find((l) => l.id === substituta1_id)?.nome || '(VAGO)';
  const nome2 = (relatorio.comarcas || [])
    .flatMap((c) => c.locais || [])
    .find((l) => l.id === substituta2_id)?.nome || '(VAGO)';

  return (
    <View style={{ flexDirection: 'column', marginBottom: 4 }}>
      <Text style={{ fontWeight: 'bold' }}>1º SUBSTITUTO</Text>
      <Text style={{ marginTop: 2 }}>{nome1}</Text>
      <Text style={{ fontWeight: 'bold', marginTop: 4 }}>2º SUBSTITUTO</Text>
      <Text style={{ marginTop: 2 }}>{nome2}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  page: { padding: 24, fontSize: 9 },
  header: { textAlign: 'center', marginBottom: 12 },
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  fullRow: { flexDirection: 'row' },
  fullCell: { flex: 1, padding: 4 },
  fullCellGrey: { backgroundColor: '#ccc' },
  fullCellGrey1: { backgroundColor: '#f0f0f0' },
  fullCellRight: { textAlign: 'right', fontSize: 8, fontStyle: 'italic' },
  gridTable: {},
  tableRow: { flexDirection: 'row', minHeight: 40 },
  tableRowOdd: { backgroundColor: '#f5f5f5' },
  tableCell: {
    width: '25%',
    padding: 4,
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  headerCell: {
    width: '25%',
    padding: 4,
    backgroundColor: '#eee',
    fontWeight: 'bold',
    textAlign: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  bold: { fontWeight: 'bold' },
  smallRedItalic: { fontSize: 7, fontStyle: 'italic', color: 'red' },
});

interface IProps {
  relatorio: IFullReport | null;
  exibirEventos: boolean;
  selectedComarcas: string[];
  selectedDefensorias: string[];
  selectedDefensores: string[];
}

const EstagiarioPorDefensoriaDinamica: React.FC<IProps> = ({
  relatorio,
  exibirEventos,
  selectedComarcas = [],
  selectedDefensorias = [],
  selectedDefensores = [],
}) => {
  if (!relatorio) {
    console.warn('Relatorio is null or undefined');
    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          <Text style={{ textAlign: 'center', fontSize: 14, fontWeight: 'bold' }}>
            Carregando ou dados indisponíveis...
          </Text>
        </Page>
      </Document>
    );
  }

  if (!relatorio.comarcas || !Array.isArray(relatorio.comarcas)) {
    console.warn('Relatorio.comarcas is undefined or not an array:', relatorio.comarcas);
    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          <Text style={{ textAlign: 'center', fontSize: 14, fontWeight: 'bold' }}>
            Dados de comarcas indisponíveis...
          </Text>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Cabeçalho */}
        <View style={styles.header}>
          <Text style={{ fontSize: 14, fontWeight: 'bold' }}>
            DEFENSORIA PÚBLICA DO ESTADO DE SERGIPE - TABELA DE LOTAÇÃO
          </Text>
          <Text style={{ fontSize: 10 }}>
            SUBDEFENSORIA - REVISADA EM {DataAmigavel(relatorio.editadoem || new Date())}
          </Text>
        </View>

        {relatorio.comarcas
          .filter((comarca): comarca is IReportComarca => {
            if (!comarca || !comarca.nome) {
              console.warn('Comarca inválida:', comarca);
              return false;
            }
            const comarcaNome = `${comarca.prefixo || ''} ${comarca.nome}`.trim();
            return selectedComarcas.length === 0 || selectedComarcas.includes(comarcaNome);
          })
          .map((comarca: IReportComarca) => (
            <View key={`comarca-${comarca.id}`} wrap>
              {/* 1ª Linha: Nome da Comarca */}
              <View style={[styles.tableWrapper, { marginBottom: 4 }]}>
                <View style={[styles.fullRow, styles.fullCellGrey]}>
                  <Text style={styles.fullCell}>{comarca.nome}</Text>
                </View>
              </View>

              {(comarca.predios || []).map((predio) => {
                const defensorias = (comarca.locais || [])
                  .filter((d) => d.predio_id === predio.id)
                  .filter((d) => selectedDefensorias.length === 0 || selectedDefensorias.includes(d.nome));

                const totalEstagiarios = defensorias.reduce((acc, defensoria) => {
                  const lotacoes = (relatorio.lotacoes || []).filter(
                    (l) => l.defensoria_id === defensoria.id && l.estagio_id && isAtivoHoje(l.data_inicial, l.data_final),
                  );
                  return acc + lotacoes.length;
                }, 0);

                return (
                  <View key={`predio-${predio.id}`} wrap style={{ marginBottom: 20 }}>
                    {/* 2ª Linha: Nome e Endereço do Prédio */}
                    <View style={styles.tableWrapper}>
                      <View style={[styles.fullRow, styles.fullCellGrey1]}>
                        <Text style={[styles.fullCell, { flex: 1 }]}>
                          - {predio.nome} - {predio.endereco?.logradouro || 'Endereço não informado'}
                        </Text>
                      </View>

                      {/* 3ª Linha: Total de Estagiários */}
                      <View style={[styles.fullRow, styles.fullCellGrey]}>
                        <Text style={[styles.fullCell, styles.fullCellRight]}>
                          Total de estagiários na comarca: {totalEstagiarios}
                        </Text>
                      </View>

                      {/* 4ª Linha: Cabeçalhos */}
                      <View style={styles.gridTable}>
                        <View style={styles.tableRow}>
                          <Text style={styles.headerCell}>LOCAL DE ATUAÇÃO</Text>
                          <Text style={styles.headerCell}>DEFENSOR</Text>
                          <Text style={styles.headerCell}>SUBSTITUIÇÃO</Text>
                          <Text style={styles.headerCell}>ESTAGIÁRIO(A)</Text>
                        </View>

                        {/* 5ª Linha em diante: Dados */}
                        {defensorias.map((defensoria: IReportLocalLotacao, index) => (
                          <View
                            key={`defensoria-${defensoria.id}`}
                            style={[styles.tableRow, index % 2 === 0 ? styles.tableRowOdd : {}]}
                            wrap={false}
                          >
                            <Text style={styles.tableCell}>{defensoria.nome}</Text>
                            <View style={styles.tableCell}>
                              {getDefensores(relatorio, defensoria.id, selectedDefensores, exibirEventos)}
                            </View>
                            <View style={styles.tableCell}>
                              {getSubstituicoes(relatorio, defensoria.substituta1_id, defensoria.substituta2_id)}
                            </View>
                            <Text style={styles.tableCell}>
                              {getEstagiarios(relatorio, defensoria.id)}
                            </Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          ))}
      </Page>
    </Document>
  );
};

export default EstagiarioPorDefensoriaDinamica;